import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';

import {
  useCreateProjectPromptSectionMutation,
  useDeleteProjectPromptSectionMutation,
  useGetGlobalPromptSectionsQuery,
  useGetProjectPromptSectionsQuery,
  useUpdateProjectPromptSectionMutation,
} from 'features/projects/api/promptSections.api';
import type { PromptSectionPermission, ProjectPromptSection } from 'features/projects/api/promptSections.api';
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import Snackbar from 'shared/ui/snackbar';
import type { SnackbarVariant } from 'shared/ui/snackbar';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

// Section identifier + default Spanish description are fixed (only value + permission are editable).
// The same section name is gated by CALENDAR_SCHEDULING in the backend prompt generator.
const SECTION_NAME = 'calendar_instructions_assignations_case';
const DEFAULT_DISPLAY_NAME = 'Instrucciones de agendamiento de reuniones';
const DEFAULT_PERMISSION: PromptSectionPermission = 'editable';

const PERMISSION_OPTIONS: { value: PromptSectionPermission; label: string }[] = [
  { value: 'hidden', label: 'Oculto' },
  { value: 'read-only', label: 'Solo lectura' },
  { value: 'suggest-edit', label: 'Sugerir edición' },
  { value: 'editable', label: 'Editable' },
];

interface Props {
  projectId: number;
}

const CalendarInstructionsAdvanced = ({ projectId }: Props): ReactElement => {
  const { data } = useGetProjectPromptSectionsQuery({
    projectId,
    offset: 0,
    limit: 100,
    searchTerm: SECTION_NAME,
  });

  // Global section provides the default value/permission shown when the project has no override yet.
  const { data: globalData } = useGetGlobalPromptSectionsQuery({ offset: 0, limit: 100, searchTerm: SECTION_NAME });

  const section: ProjectPromptSection | undefined = data?.promptSections.find((s) => s.name === SECTION_NAME);
  const sectionExists = Boolean(section);
  const globalSection = globalData?.globalPromptSections.find((s) => s.name === SECTION_NAME);

  const [createSection, { isLoading: isCreating }] = useCreateProjectPromptSectionMutation();
  const [updateSection, { isLoading: isUpdating }] = useUpdateProjectPromptSectionMutation();
  const [deleteSection, { isLoading: isDeleting }] = useDeleteProjectPromptSectionMutation();

  const [open, setOpen] = useState(false);
  const [valueDraft, setValueDraft] = useState('');
  const [permissionDraft, setPermissionDraft] = useState<PromptSectionPermission>(DEFAULT_PERMISSION);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; variant: SnackbarVariant }>({
    open: false,
    message: '',
    variant: 'success',
  });

  // Sync drafts with the persisted section; when the project has no override yet, fall back to the
  // global section's value/permission so the editor opens pre-filled with the current default.
  useEffect(() => {
    if (section) {
      setValueDraft(section.value ?? '');
      setPermissionDraft(section.permission ?? DEFAULT_PERMISSION);
    } else {
      setValueDraft(globalSection?.value ?? '');
      setPermissionDraft(globalSection?.permission ?? DEFAULT_PERMISSION);
    }
  }, [section?.id, section?.value, section?.permission, globalSection?.value, globalSection?.permission]);

  const isSaving = isCreating || isUpdating;

  // When the project has no override yet, "Guardar" is always enabled (it creates the override from
  // the prefilled global default). When an override exists, it's only enabled if the drafts changed.
  const canSave = section ? valueDraft !== section.value || permissionDraft !== section.permission : true;

  const notify = (message: string, variant: SnackbarVariant): void => setSnackbar({ open: true, message, variant });

  const handleSave = async (): Promise<void> => {
    if (!valueDraft.trim()) {
      notify('El valor no puede estar vacío.', 'error');
      return;
    }
    try {
      if (section) {
        await updateSection({
          id: section.id,
          projectId,
          name: SECTION_NAME,
          displayName: section.displayName || DEFAULT_DISPLAY_NAME,
          value: valueDraft,
          permission: permissionDraft,
        }).unwrap();
      } else {
        await createSection({
          projectId,
          name: SECTION_NAME,
          displayName: DEFAULT_DISPLAY_NAME,
          value: valueDraft,
          permission: permissionDraft,
        }).unwrap();
      }
      notify('Instrucciones de agendamiento guardadas.', 'success');
    } catch {
      notify('No se pudieron guardar las instrucciones.', 'error');
    }
  };

  const handleDelete = async (): Promise<void> => {
    try {
      await deleteSection({ projectId, name: SECTION_NAME }).unwrap();
      setValueDraft('');
      setPermissionDraft(DEFAULT_PERMISSION);
      setOpen(false);
      notify('Instrucciones de agendamiento eliminadas.', 'success');
    } catch {
      notify('No se pudieron eliminar las instrucciones.', 'error');
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-1.5 py-1 text-sm font-semibold text-text-brand"
      >
        Configuración avanzada
        {open ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
      </button>

      {open && (
        <div className="mt-3 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="calendar-instructions-value" className="text-sm font-medium">
              Configuración prompt section
            </Label>
            <Textarea
              id="calendar-instructions-value"
              className="min-h-28"
              placeholder="Escribe el valor..."
              value={valueDraft}
              disabled={isSaving || isDeleting}
              onChange={(e) => setValueDraft(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="calendar-instructions-permission" className="text-sm font-medium">
              Permiso
            </Label>
            <Select
              value={permissionDraft}
              disabled={isSaving || isDeleting}
              onValueChange={(v) => setPermissionDraft(v as PromptSectionPermission)}
            >
              <SelectTrigger id="calendar-instructions-permission" className="w-full">
                <SelectValue placeholder="Seleccionar permiso" />
              </SelectTrigger>
              <SelectContent>
                {PERMISSION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between gap-3">
            {sectionExists ? (
              <Button
                variant="outline"
                size="sm"
                className="border-error text-error hover:bg-error-light hover:text-error"
                disabled={isDeleting || isSaving}
                onClick={() => {
                  void handleDelete();
                }}
              >
                <Trash2 className="size-4" />
                Eliminar
              </Button>
            ) : (
              <span />
            )}
            <Button
              size="sm"
              disabled={isSaving || isDeleting || !canSave}
              onClick={() => {
                void handleSave();
              }}
            >
              {isSaving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        variant={snackbar.variant}
        message={snackbar.message}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default CalendarInstructionsAdvanced;
